import { useMemo, useState } from "react";
import { ColDef } from "ag-grid-community";
import { useLoaderData } from "react-router-dom";
import { Container, Row, Col } from "react-bootstrap";
import { AddVehicleModal } from "./Controls/AddVehicleModal";
import { Vehicle, VehicleDetails } from "./Data/ApiTypes";
import { push } from "./Data/Util";
import { PageBar } from "./Controls/PageBar";
import { DataGrid } from "./Controls/DataGrid";
import { gridOneCol, useParamIds } from "./Data/Common";
import { VehicleLinkType } from "./Data/ApiTransport";
import { Hyperlink } from "./Controls/Hyperlink";
import { showStateName, getStateEnumFromCode } from "./Data/UsState";
import { Button } from "./Controls/Button";
import { Api } from "./Data/Api";
import { toast } from "react-toastify";
import { ConfirmModal } from "./Controls/ConfirmModal";
import { Trash } from "react-bootstrap-icons";

export function FacilityParkerVehicleListPage() {
  const { facilityId, parkerId } = useParamIds();
  const loadedVehicles = useLoaderData() as VehicleDetails[];
  const [vehicles, setVehicles] = useState([...loadedVehicles]);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [showRemove, setShowRemove] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState<number | null>(
    null
  );
  const [vehicleToEdit, setVehicleToEdit] = useState<Vehicle | undefined>(
    undefined
  );

  async function handleSubmitVehicle(vehicle: Vehicle) {
    if (vehicleToEdit) {
      updateVehicle(vehicle);
    } else {
      addVehicle(vehicle);
    }

    setVehicleToEdit(undefined);
    setShowAddVehicle(false);
  }

  async function addVehicle(vehicle: Vehicle) {
    const result = await Api.vehicleAdd(facilityId, parkerId, vehicle);

    if (result.isOk && result.value) {
      setVehicles(push(vehicles, { ...result.value, links: [] }));
      toast.success("Vehicle Added Successfully");
    } else {
      const resultError = result as any;
      toast.error(resultError.error);
    }
  }

  async function updateVehicle(vehicle: Vehicle) {
    const result = await Api.vehicleUpdate(
      facilityId,
      parkerId,
      vehicleToEdit!.vehicleId,
      vehicle
    );

    if (result.isOk && result.value) {
      setVehicles((prev) =>
        prev.map((veh) =>
          veh.vehicleId === vehicleToEdit!.vehicleId
            ? { ...veh, ...result.value }
            : veh
        )
      );
      toast.success("Vehicle Updated Successfully");
    } else {
      const resultError = result as any;
      toast.error(resultError.error);
    }
  }

  async function removeVehicle(
    facId: number,
    parkerId: number,
    vehicleId: number
  ) {
    const result = await Api.vehicleDelete(facId, parkerId, vehicleId);

    if (result.isOk) {
      setVehicles((prev) => prev.filter((v) => v.vehicleId !== vehicleId));
      toast.success("Vehicle Removed Successfully");
    } else {
      toast.error("Failed To Remove Vehicle");
    }
  }

  const columns = useMemo<ColDef[]>(
    () => [
      {
        headerName: "Make",
        field: "make",
        sortable: true,
        filter: true,
        cellRenderer: (params: any) => {
          const make = params.value;

          return (
            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Hyperlink
                onClick={() => {
                  setVehicleToEdit(params.data);
                  setShowAddVehicle(true);
                }}
              >
                {make}
              </Hyperlink>
            </span>
          );
        },
      },
      {
        headerName: "Model",
        field: "model",
        sortable: true,
        filter: true,
      },
      {
        headerName: "Color",
        field: "color",
        cellRenderer: (p: any) => (
          <div className="d-flex align-items-center gap-2">
            <div
              style={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                backgroundColor: p.value,
                border: "1px solid #ccc",
              }}
            />
            {p.value}
          </div>
        ),
      },
      {
        headerName: "State",
        valueGetter: (p) =>
          `${showStateName(getStateEnumFromCode(p.data.stateCode)!) || ""}`,
      },
      {
        headerName: "License Plate",
        valueGetter: (p) => p.data.licensePlateNumber,
      },
      {
        colId: "usedOn",
        field: "usedOn",
        headerName: "Used On",
        filter: "agTextColumnFilter",
        valueGetter: (params) => {
          const links = params.data?.links ?? [];
          const res = links.filter(
            (l: any) => l.type === VehicleLinkType.Reservation
          );
          const subs = links.filter(
            (l: any) => l.type === VehicleLinkType.Subscription
          );

          if (res.length === 0 && subs.length === 0) return "Not Used";

          return [
            ...res.map((l) => `Reservation #${l.reservationId}`),
            ...subs.map((l) => `Plan #${l.subscriptionId}`),
          ].join(", ");
        },
        filterValueGetter: (params) => {
          const links = params.data?.links ?? [];
          const texts: string[] = [];

          for (const link of links) {
            if (
              link.type === VehicleLinkType.Reservation &&
              link.reservationId
            ) {
              texts.push(`Used on Reservation`);
            }
            if (
              link.type === VehicleLinkType.Subscription &&
              link.subscriptionId
            ) {
              texts.push(`Used on Plan #${link.subscriptionId}`);
            }
          }

          return texts.length ? texts.join(", ") : "Not Used";
        },
        getQuickFilterText: (params) => {
          const links = params.data?.links ?? [];
          const texts: string[] = [];

          for (const link of links) {
            if (
              link.type === VehicleLinkType.Reservation &&
              link.reservationId
            ) {
              texts.push(`Used on Reservation`);
            }
            if (
              link.type === VehicleLinkType.Subscription &&
              link.subscriptionId
            ) {
              texts.push(`Used on Plan #${link.subscriptionId}`);
            }
          }

          return texts.length ? texts.join(", ") : "Not Used";
        },
        cellRenderer: (p: any) => {
          const ls = p.data.links || [];
          const res = ls.filter(
            (l: any) => l.type === VehicleLinkType.Reservation
          );
          const subs = ls.filter(
            (l: any) => l.type === VehicleLinkType.Subscription
          );

          if (res.length === 0 && subs.length === 0) {
            return <span className="text-muted">Not Used</span>;
          }

          return (
            <div className="d-flex flex-column">
              {res.map((r: any) => (
                <span key={`res-${r.reservationId}`}>
                  Used on Reservation
                
                </span>
              ))}
              {subs.map((s: any, index: number) => (
                <span key={`sub-${s.subscriptionId}-${index}`}>
                  Used on Plan{" "}
                  <Hyperlink
                    to={`/facility/${facilityId}/parker/${parkerId}/plan/${s.subscriptionId}`}
                  >
                    #{s.subscriptionId}
                  </Hyperlink>
                </span>
              ))}
            </div>
          );
        },
      },
      {
        field: "",
        headerName: "",
        sortable: false,
        filter: false,
        width: 80,
        maxWidth: 80,
        cellRenderer: (params: any) => {
          return (
           <button
              className="btn btn-sm"
              title="Delete Vehicle"
              onClick={() => {
                setSelectedVehicleId(params.data.vehicleId);
                setShowRemove(true);
              }}
              >
            <Trash />
          </button>
          );
        },
      },
    ],
    [facilityId, parkerId]
  );

  return (
    <Container fluid className="h-100">
      <Row className="h-100">
        <Col style={gridOneCol("max-content 1fr 5px")}>
          <div className="d-flex justify-content-between align-items-center mt-2 mb-2">
            <PageBar title="Vehicles" />
            <Button
              className="btn-main"
              onClick={() => setShowAddVehicle(true)}
            >
              Add Vehicle
            </Button>
          </div>
          <div style={{ height: "100%", width: "100%" }}>
            <DataGrid
              rowData={[...vehicles].sort((a, b) =>
                a.make.localeCompare(b.make)
              )}
              columnDefs={columns}
            />
          </div>
        </Col>
      </Row>

      <AddVehicleModal
        show={showAddVehicle}
        setShow={setShowAddVehicle}
        onSubmit={handleSubmitVehicle}
        vehicleToEdit={vehicleToEdit}
      />
      <ConfirmModal
        title="Remove Vehicle"
        message="Are you sure you want to remove this vehicle?"
        show={showRemove}
        setShow={setShowRemove}
        onConfirm={() => {
          if (selectedVehicleId !== undefined) {
            removeVehicle(facilityId, parkerId, selectedVehicleId!);
          }
        }}
      />
    </Container>
  );
}
